import { useQuery } from '@tanstack/vue-query'
import debounce from 'debounce'

import { locationService, useGolfsService, useRegionsService } from '~/core/services'
import { type LocationItem, LocationType } from '~/core/types'
import { BNGPartners } from '~/partner.type'

export function useSearch() {
  const { getRegionsAutocomplete } = useRegionsService()
  const { getGolfsAutocomplete } = useGolfsService()
  const activeSearchValue = ref('')
  const partner = ref<BNGPartners>()
  const debounceDelay = ref(400)

  function handleLocationInputSearch(value: string) {
    activeSearchValue.value = value
  }

  const debouncedHandleLocationInputSearch = debounce(
    handleLocationInputSearch,
    debounceDelay.value,
  )

  const canFetch = computed(() => activeSearchValue.value !== null && activeSearchValue.value.length > 2)
  const begolf = computed(() => partner.value === BNGPartners.BLUE_GREEN ? true : null)

  const { data: autocompleteGolfs } = useQuery({
    enabled: canFetch,
    queryKey: ['autocomplete-golfs', activeSearchValue],
    queryFn: () => getGolfsAutocomplete(begolf.value
      ? { nom: activeSearchValue.value, begolf: begolf.value }
      : { nom: activeSearchValue.value },
    ),
  })

  const { data: cities } = useQuery({
    enabled: canFetch,
    queryKey: ['cities', activeSearchValue],
    queryFn: () => locationService.fetchCities(activeSearchValue.value),
  })

  const { data: departments } = useQuery({
    enabled: canFetch,
    queryKey: ['departments', activeSearchValue],
    queryFn: () => locationService.fetchDepartments({ nom: activeSearchValue.value }),
  })

  const { data: regions } = useQuery({
    enabled: canFetch,
    queryKey: ['regions', activeSearchValue],
    queryFn: () => getRegionsAutocomplete(activeSearchValue.value),
  })

  const golfs = computed(() => autocompleteGolfs.value?.data?.map((golf) => ({
    ...golf,
    _highlightResult: highlightResults(golf.name, activeSearchValue.value),
  })) ?? [])

  const groups = computed((): { label: string, options: LocationItem[], type: LocationType }[] => [
    {
      label: 'Golfs',
      type: LocationType.GOLF,
      options: golfs.value?.map((g) => ({
        type: LocationType.GOLF,
        fancyLabel: highlightResults(`${g.name}`, activeSearchValue.value).value,
        value: g,
      })) ?? [],
    },
    {
      label: 'Villes',
      type: LocationType.CITY,
      options: cities.value?.map((c) => ({
        type: LocationType.CITY,
        fancyLabel: highlightResults(`${c.nom}, ${c.departement?.nom ?? ''}`, activeSearchValue.value).value,
        value: c,
      })) ?? [],
    },
    {
      label: 'Départements',
      type: LocationType.DEPARTMENT,
      options: departments.value?.map((d) => ({
        type: LocationType.DEPARTMENT,
        fancyLabel: highlightResults(`${d.nom}`, activeSearchValue.value).value,
        value: d,
      })) ?? [],
    },
    {
      label: 'Régions',
      type: LocationType.REGION,
      options: regions.value?.data?.map((r) => ({
        type: LocationType.REGION,
        fancyLabel: highlightResults(`${r.name}`, activeSearchValue.value).value,
        value: r,
      })) ?? [],
    },
  ])

  const noResult = computed(() =>
    cities.value?.length === 0
    && golfs.value?.length === 0
    && regions.value?.data?.length === 0
    && departments.value?.length === 0,
  )

  return {
    groups,
    activeSearchValue,
    partner,
    debouncedHandleLocationInputSearch,
    noResult,
  }
}
